import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import MentionTextarea from '../components/MentionTextarea';
import { SurfacePage, SoftSurface } from '../components/surface';
import { ClubLogo } from '../components/club/ClubCover';
import {
  ClubPanel, ClubBackPill, PillButton, RingTab, TagPill, useArmed, useToast, Toast, ErrorBand,
  MUTED, SUBTLE, CHIP, INSET, PINNED, RULE, inputStyle, panelTitle, metaRight,
} from '../components/club/clubUi';
import clubService from '../services/clubService';
import { useDialog } from '../context/DialogContext';
import useIsMobile from '../hooks/useIsMobile';
import { ago, plural } from '../utils/surfaceFormat';
import { tintFor } from '../utils/tints';

/**
 * /clubs/:id/forum — design_handoff_club_pages/README.md §3 "Forum". Header card (logo, `CLUB ·
 * MEMBERS ONLY`, Forum, NEW THREAD / CANCEL), a sticky CATEGORIES card on the left (chips on the
 * phone), the NEW THREAD composer with @mentions, then the thread rows. `?category=` filters;
 * `#new` opens the composer. docs/plans/2026-08-club-forum/PLAN.md for the data layer.
 */

const TEXTAREA_SOFT = '!border-0 !bg-[#F5F5F5] rounded-[20px]';

function Avatar({ src, name, tintKey, size = 44 }) {
  return (
    <span className="flex flex-none items-center justify-center overflow-hidden rounded-full" style={{ width: size, height: size, background: src ? '#E5E7EB' : tintFor(tintKey || name), fontSize: Math.round(size * 0.36), fontWeight: 800 }}>
      {src ? <img src={src} alt="" referrerPolicy="no-referrer" className="block h-full w-full object-cover" /> : (name || '?').charAt(0).toUpperCase()}
    </span>
  );
}

export default function ClubForumPage() {
  const { id: clubId } = useParams();
  const navigate = useNavigate();
  const { showAlert } = useDialog();
  const phone = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryId = searchParams.get('category') || '';
  const { toast, say } = useToast();
  const { isArmed, confirm, disarm } = useArmed();

  const [forum, setForum] = useState(null);
  const [club, setClub] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [composing, setComposing] = useState(() => (typeof window !== 'undefined' && window.location.hash === '#new'));
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [newCategoryId, setNewCategoryId] = useState('');
  const [tagged, setTagged] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await clubService.getForumOverview(clubId, categoryId || null);
    if (r?.success) { setForum(r.data); setError(null); }
    else setError(r?.error?.message || r?.error || 'Could not open this forum');
    setLoading(false);
  }, [clubId, categoryId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      const [m, c] = await Promise.all([clubService.listForumMembers(clubId), clubService.getClub(clubId)]);
      if (m?.success) setMembers(m.data || []);
      if (c?.success) setClub(c.data || null);
    })();
  }, [clubId]);

  const setCategory = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('category', id); else next.delete('category');
    setSearchParams(next);
  };

  const create = async () => {
    if (!title.trim() || busy) return;
    setBusy(true);
    const r = await clubService.createForumThread(clubId, {
      title: title.trim(),
      body: body.trim() || null,
      categoryId: newCategoryId || null,
      // Ids cover members whose token isn't a resolvable @handle.
      mentionedUserIds: tagged.map((t) => t.userId),
    });
    setBusy(false);
    if (r?.success) {
      setComposing(false); setTitle(''); setBody(''); setTagged([]); setNewCategoryId('');
      navigate(`/clubs/${clubId}/forum/${r.data.id}`);
    } else {
      showAlert({ title: 'COULD NOT POST', message: r?.error?.message || r?.error || 'Try again.', type: 'error' });
    }
  };

  const togglePin = async (thread) => {
    await clubService.setForumThreadPinned(thread.id, !thread.isPinned);
    load();
  };

  // Two-step: the first tap arms the row's DELETE into CONFIRM, the second deletes.
  const remove = async (thread) => {
    if (!confirm(`del:${thread.id}`)) return;
    const r = await clubService.deleteForumThread(thread.id);
    if (r && r.success === false) { showAlert({ title: 'COULD NOT DELETE', message: r.error?.message || r.error || 'Try again.', type: 'error' }); return; }
    say('THREAD DELETED');
    load();
  };

  const categories = useMemo(() => forum?.categories || [], [forum]);
  const threads = useMemo(() => [...(forum?.threads || [])].sort((a, b) => (b.isPinned ? 1 : 0) - (a.isPinned ? 1 : 0)), [forum]);
  const activeName = useMemo(() => (categoryId ? (categories.find((c) => c.id === categoryId)?.name || 'THREADS').toUpperCase() : 'ALL THREADS'), [categories, categoryId]);
  const clubName = forum?.clubName || club?.name || 'CLUB';
  const taggedNote = tagged.length
    ? `Tagging ${tagged.map((t) => (t.displayName || t.token || '').replace(/^@/, '').split(' ')[0]).filter(Boolean).join(', ')}. They get a notification.`
    : 'Members of this club only.';

  const frame = (children) => (
    <SoftSurface>
      <SurfacePage bare showTopBarOnMobile>
        <main className="mx-auto flex w-full flex-col font-mono" style={{ maxWidth: 1180, padding: phone ? '12px 12px 24px' : '24px 32px 72px', gap: phone ? 10 : 16, boxSizing: 'border-box' }} data-testid="club-forum">
          <div className="flex items-center" style={{ gap: 8 }}>
            <ClubBackPill to={`/clubs/${clubId}`}>{clubName}</ClubBackPill>
          </div>
          {children}
        </main>
        <Toast text={toast} phone={phone} />
      </SurfacePage>
    </SoftSurface>
  );

  if (loading && !forum) return frame(<div className="py-16"><LoadingSpinner label="OPENING THE FORUM…" /></div>);
  if (error) return frame(<ErrorBand title="NOT OPEN" testId="forum-error">{error}</ErrorBand>);

  const catTabs = [['', 'ALL', forum?.totalThreadCount ?? threads.length], ...categories.map((c) => [c.id, c.name, c.threadCount])];

  const categoriesCard = (
    <section className="sticky flex flex-col bg-white" style={{ top: 88, borderRadius: phone ? 22 : 24, padding: 18, gap: 6 }} data-testid="forum-categories">
      <span style={{ ...metaRight, color: MUTED, padding: '0 6px 8px' }}>CATEGORIES</span>
      {catTabs.map(([k, l, n]) => (
        <RingTab key={k || 'all'} on={categoryId === k} off={INSET} height={44} fontSize={11} tracking=".1em" onClick={() => setCategory(k)} style={{ justifyContent: 'space-between', padding: '0 16px', textAlign: 'left' }}>
          <span className="truncate">{l}</span><span style={{ opacity: 0.6 }}>{n}</span>
        </RingTab>
      ))}
      {forum?.canManageCategories && (
        <button type="button" onClick={() => navigate(`/clubs/${clubId}/manage?tab=features#forum-categories`)} className="text-left font-mono uppercase" style={{ marginTop: 8, padding: '10px 6px 0', border: 'none', borderTop: RULE, background: 'none', fontSize: 10, fontWeight: 700, letterSpacing: '.14em', color: MUTED, cursor: 'pointer' }} data-testid="manage-categories">
          MANAGE CATEGORIES →
        </button>
      )}
    </section>
  );

  const chips = (
    <div className="flex" style={{ gap: 6, overflowX: 'auto', scrollbarWidth: 'none' }} data-testid="forum-category-chips">
      {catTabs.map(([k, l, n]) => (
        <RingTab key={k || 'all'} on={categoryId === k} height={36} fontSize={10} tracking=".1em" onClick={() => setCategory(k)} style={{ padding: '0 14px' }}>
          {l}<span style={{ opacity: 0.6 }}>{n}</span>
        </RingTab>
      ))}
    </div>
  );

  const composer = composing && (
    <ClubPanel phone={phone} style={{ gap: 12 }} data-testid="new-thread">
      <span style={panelTitle}>NEW THREAD</span>
      <input type="text" value={title} onChange={(e) => setTitle(e.target.value.slice(0, 160))} maxLength={160} placeholder="What’s this about?" autoFocus
        style={{ ...inputStyle, height: 52, fontSize: 16, fontWeight: 700 }} aria-label="Thread title" />
      <MentionTextarea value={body} onChange={setBody} people={members} onTag={(t) => setTagged((prev) => (prev.some((x) => x.userId === t.userId) ? prev : [...prev, t]))} rows={4} placeholder="Say more. Type @ to tag a member." className={TEXTAREA_SOFT} />
      <span className="flex flex-wrap items-center" style={{ gap: 6 }}>
        <span style={{ ...metaRight, color: MUTED, fontSize: 10, paddingRight: 4 }}>IN</span>
        {[['', 'NO CATEGORY'], ...categories.map((c) => [c.id, c.name])].map(([k, l]) => (
          <button key={k || 'none'} type="button" onClick={() => setNewCategoryId(k)} aria-pressed={newCategoryId === k} className="font-mono uppercase"
            style={{ height: 34, padding: '0 14px', border: 'none', borderRadius: 999, background: newCategoryId === k ? '#000' : CHIP, color: newCategoryId === k ? '#fff' : '#000', fontSize: 10, fontWeight: 700, letterSpacing: '.1em', cursor: 'pointer' }}>
            {l}
          </button>
        ))}
      </span>
      <span className="flex flex-wrap items-center" style={{ gap: 10 }}>
        <span style={{ fontSize: 11, color: MUTED }}>{taggedNote}</span>
        <PillButton size={46} onClick={create} disabled={busy || !title.trim()} style={{ marginLeft: 'auto', padding: '0 24px', opacity: title.trim() ? 1 : 0.35 }} data-testid="post-thread">{busy ? 'POSTING…' : 'POST THREAD'}</PillButton>
      </span>
    </ClubPanel>
  );

  const list = (
    <ClubPanel phone={phone} style={{ gap: 0 }} data-testid="thread-list">
      <span className="flex justify-between" style={{ gap: 10, paddingBottom: 6 }}>
        <span style={panelTitle}>{activeName}</span>
        <span style={metaRight}>{plural(threads.length, 'THREAD')}</span>
      </span>
      {threads.length === 0 && (
        <div className="flex flex-col items-center text-center" style={{ gap: 8, marginTop: 8, padding: '36px 20px', borderRadius: 20, background: INSET }} data-testid="forum-empty">
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.16em', color: SUBTLE }}>NOTHING HERE YET</span>
          <span style={{ fontSize: 12, lineHeight: 1.6, color: MUTED }}>{categoryId ? 'Start the first thread in this category.' : 'Start the first thread.'}</span>
          <PillButton size={36} onClick={() => setComposing(true)} style={{ marginTop: 6 }}>NEW THREAD</PillButton>
        </div>
      )}
      {threads.map((t, i) => {
        const armed = isArmed(`del:${t.id}`);
        return (
          <div key={t.id} className="flex flex-wrap items-center" style={{ gap: '10px 14px', padding: '14px 0', borderTop: i ? RULE : 'none' }} data-testid={`thread-row-${t.id}`}>
            <Avatar src={t.authorAvatarUrl} name={t.authorName} tintKey={t.authorUserId || t.id} />
            <button type="button" onClick={() => navigate(`/clubs/${clubId}/forum/${t.id}`)} className="flex min-w-0 flex-col text-left font-mono" style={{ flex: '1 1 220px', gap: 5, border: 'none', background: 'none', padding: 0, cursor: 'pointer', color: 'inherit' }}>
              <span className="flex flex-wrap items-center" style={{ gap: 6 }}>
                {t.isPinned && <TagPill bg={PINNED} ring="#000" data-testid={`pinned-${t.id}`}>PINNED</TagPill>}
                {t.categoryName && <TagPill>{t.categoryName}</TagPill>}
              </span>
              <span style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.35 }}>{t.title}</span>
              <span style={{ fontSize: 11, color: MUTED }}>{t.authorName} · {plural(t.replyCount || 0, 'REPLY', 'REPLIES')}{ago(t.lastActivityAt || t.createdAt) ? ` · LAST ${ago(t.lastActivityAt || t.createdAt)}` : ''}</span>
            </button>
            <span className="flex flex-none items-center" style={{ gap: 6 }}>
              <span className="flex items-center justify-center" style={{ minWidth: 44, height: 34, padding: '0 12px', boxSizing: 'border-box', borderRadius: 999, background: INSET, gap: 6, fontSize: 11, fontWeight: 800 }} aria-label={`${t.replyCount || 0} replies`}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" aria-hidden="true"><path d="M3 4h18v13H8l-5 4z" /></svg>{t.replyCount || 0}
              </span>
              {t.canPin && <PillButton variant="tertiary" size={34} fontSize={9} tracking=".12em" onClick={() => togglePin(t)} aria-label={t.isPinned ? `Unpin ${t.title}` : `Pin ${t.title}`}>{t.isPinned ? 'UNPIN' : 'PIN'}</PillButton>}
              {t.canModerate && (
                <PillButton variant={armed ? 'armed' : 'danger'} size={34} fontSize={9} tracking=".12em" onClick={() => remove(t)} onBlur={armed ? disarm : undefined} aria-label={armed ? `Confirm delete ${t.title}` : `Delete ${t.title}`} data-testid={`delete-${t.id}`}>
                  {armed ? 'CONFIRM' : 'DELETE'}
                </PillButton>
              )}
            </span>
          </div>
        );
      })}
    </ClubPanel>
  );

  return frame(
    <>
      <section className="flex flex-wrap items-center bg-white" style={{ gap: '14px 18px', padding: phone ? 16 : '20px 24px', borderRadius: phone ? 22 : 24 }} data-testid="forum-header">
        <ClubLogo club={{ id: clubId, name: clubName, avatarUrl: club?.avatarUrl }} size={phone ? 52 : 64} radius={16} fontSize={phone ? 22 : 28} />
        <span className="flex min-w-0 flex-col" style={{ flex: '1 1 200px', gap: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.18em', color: MUTED, textTransform: 'uppercase' }}>{clubName} · MEMBERS ONLY</span>
          <h1 className="m-0" style={{ fontSize: phone ? 24 : 32, fontWeight: 700, lineHeight: 1.1, textTransform: 'none' }}>Forum</h1>
        </span>
        <PillButton variant={composing ? 'secondary' : 'primary'} size={46} fontSize={11} onClick={() => setComposing((v) => !v)} style={{ padding: '0 22px' }} data-testid="new-thread-toggle">{composing ? 'CANCEL' : 'NEW THREAD'}</PillButton>
      </section>

      <div className="grid items-start" style={{ gridTemplateColumns: phone ? 'minmax(0,1fr)' : '260px minmax(0,1fr)', gap: phone ? 10 : 16 }}>
        {!phone && categoriesCard}
        <div className="flex min-w-0 flex-col" style={{ gap: phone ? 10 : 16 }}>
          {phone && chips}
          {composer}
          {list}
        </div>
      </div>
    </>,
  );
}
